import React, { useState } from 'react';
import { X, Plus } from 'lucide-react';
import LayoutWithChat from './LayoutWithChat';
import ResponsiveTable, { Column } from './ResponsiveTable';

interface Player {
  id: string;
  name: string;
  position: string;
  team: string;
  photoUrl?: string;
  age?: number;
  height?: string;
  weight?: string;
  experience?: string;
  slot?: 'player1' | 'player2';
  stats?: {
    points: number;
    projectedPoints: number;
    rank: number;
    targets?: number;
    carries?: number;
    receptions?: number;
    yards: number;
    touchdowns: number;
  };
}

interface ComparisonCategory {
  name: string;
  winner: 'player1' | 'player2';
  reasoning: string;
}

interface PlayerAnalysis {
  winner: 'player1' | 'player2';
  winnerName: string;
  summary: string;
  categories: ComparisonCategory[];
}

interface PlayerComparisonProps {}

const PlayerComparison: React.FC<PlayerComparisonProps> = () => {
  const [player1, setPlayer1] = useState<Player | null>({
    id: '1',
    name: "Ja'Marr Chase",
    position: 'WR',
    team: 'CIN',
    photoUrl: 'https://www.pro-football-reference.com/req/20230307/images/headshots/ChasJa00_2024.jpg',
    age: 24,
    height: '6\'0"',
    weight: '201 lbs',
    experience: '3rd year',
    stats: {
      points: 18.2,
      projectedPoints: 19.5,
      rank: 3,
      targets: 8.5,
      receptions: 6.2,
      yards: 89.3,
      touchdowns: 0.8
    }
  });

  const [player2, setPlayer2] = useState<Player | null>({
    id: '2',
    name: 'CeeDee Lamb',
    position: 'WR',
    team: 'DAL',
    photoUrl: 'https://www.pro-football-reference.com/req/20230307/images/headshots/LambCe00_2024.jpg',
    age: 25,
    height: '6\'2"',
    weight: '198 lbs',
    experience: '4th year',
    stats: {
      points: 17.8,
      projectedPoints: 18.9,
      rank: 5,
      targets: 9.1,
      receptions: 6.8,
      yards: 85.7,
      touchdowns: 0.7
    }
  });

  const [analysis] = useState<PlayerAnalysis>({
    winner: 'player1',
    winnerName: "Ja'Marr Chase",
    summary: 'Chase edges out Lamb with superior red zone usage and higher ceiling, though both are elite WR1 options with similar floor values.',
    categories: [
      {
        name: 'Current Performance',
        winner: 'player1',
        reasoning: 'Chase is averaging 0.4 more fantasy points per game with more explosive plays and red zone targets.'
      },
      {
        name: 'Target Share',
        winner: 'player2',
        reasoning: 'Lamb sees slightly more targets per game (9.1 vs 8.5) and has a more consistent target floor.'
      },
      {
        name: 'Red Zone Usage',
        winner: 'player1',
        reasoning: 'Chase has significantly more red zone targets and goal line opportunities, leading to higher TD upside.'
      },
      {
        name: 'Playoff Schedule',
        winner: 'player2',
        reasoning: 'Dallas has a more favorable playoff schedule with better matchups against weaker pass defenses.'
      },
      {
        name: 'Injury Risk',
        winner: 'player2',
        reasoning: 'Lamb has been more durable throughout his career with fewer injury concerns than Chase.'
      },
      {
        name: 'Ceiling/Upside',
        winner: 'player1',
        reasoning: 'Chase has shown higher weekly ceiling with multiple 30+ point games this season.'
      }
    ]
  });



  const getInitials = (name: string): string => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Player comparison columns configuration
  const playerComparisonColumns: Column[] = [
    {
      key: 'player',
      label: 'Player',
      width: '40%',
      className: 'font-medium text-text-primary',
      render: (value, player) => (
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-bg-tertiary text-xs font-medium text-text-secondary">
            {player.avatar ? (
              <img 
                src={player.avatar} 
                alt={player.name} 
                className="h-full w-full rounded-full object-cover"
              />
            ) : (
              getInitials(player.name)
            )}
          </div>
          <div>
            <div className="font-medium text-text-primary">{player.name}</div>
            <div className="text-xs text-text-secondary">{player.position} • {player.team}</div>
          </div>
        </div>
      )
    },
    {
      key: 'rank',
      label: 'Rank',
      width: '10%',
      className: 'text-text-secondary text-center',
      render: (value, player) => `#${player.stats?.rank || '-'}`
    },
    {
      key: 'points',
      label: 'PPG',
      width: '15%',
      className: 'text-text-secondary text-center',
      render: (value, player) => player.stats?.points.toFixed(1) || '-'
    },
    {
      key: 'projected',
      label: 'Proj',
      width: '15%',
      className: 'text-text-secondary text-center',
      render: (value, player) => player.stats?.projectedPoints.toFixed(1) || '-'
    },
    {
      key: 'yards',
      label: 'Yards',
      width: '15%',
      className: 'text-text-secondary text-center',
      render: (value, player) => player.stats?.yards.toFixed(1) || '-'
    },
    {
      key: 'actions',
      label: '',
      width: '5%',
      className: '',
      render: (value, player) => (
        <button 
          onClick={(e) => {
            e.stopPropagation();
            removePlayer(player.slot);
          }}
          className="text-text-secondary hover:text-text-primary transition-colors"
        >
          <X size={20} />
        </button>
      )
    }
  ];

  // Player stats columns configuration
  const statsColumns: Column[] = [
    {
      key: 'stat',
      label: 'Stat',
      width: '40%',
      className: 'text-sm font-medium text-text-primary',
      render: (value) => value
    },
    {
      key: 'player1',
      label: player1?.name || 'Player 1',
      width: '30%',
      className: 'text-sm text-text-secondary text-center',
      render: (value) => value
    },
    {
      key: 'player2',
      label: player2?.name || 'Player 2',
      width: '30%',
      className: 'text-sm text-text-secondary text-center',
      render: (value) => value
    }
  ];

  // Analysis columns configuration
  const analysisColumns: Column[] = [
    {
      key: 'name',
      label: 'Category',
      width: '25%',
      className: 'text-sm font-medium text-text-primary',
      render: (value) => value
    },
    {
      key: 'winner',
      label: 'Better Player',
      width: '20%',
      className: 'text-center',
      render: (value, category) => (
        <span className={`text-sm font-medium ${
          category.winner === 'player1' ? 'text-green-400' : 'text-blue-400'
        }`}>
          {category.winner === 'player1' ? player1?.name : player2?.name}
        </span>
      )
    },
    {
      key: 'reasoning',
      label: 'Analysis',
      width: '55%',
      className: 'text-sm text-text-secondary',
      render: (value) => value
    }
  ];

  const removePlayer = (playerSlot: 'player1' | 'player2') => {
    if (playerSlot === 'player1') {
      setPlayer1(null);
    } else {
      setPlayer2(null);
    }
  };

  const addPlayer = (playerSlot: 'player1' | 'player2') => {
    // Placeholder function for adding players
    console.log(`Add player to ${playerSlot}`);
  };

  // Generate stats comparison data
  const statsData = player1 && player2 ? [
    {
      stat: 'Fantasy Points/Game',
      player1: player1.stats?.points.toFixed(1) || '-',
      player2: player2.stats?.points.toFixed(1) || '-'
    },
    {
      stat: 'Projected Points',
      player1: player1.stats?.projectedPoints.toFixed(1) || '-',
      player2: player2.stats?.projectedPoints.toFixed(1) || '-'
    },
    {
      stat: 'Position Rank',
      player1: `#${player1.stats?.rank || '-'}`,
      player2: `#${player2.stats?.rank || '-'}`
    },
    {
      stat: 'Targets/Game',
      player1: player1.stats?.targets?.toFixed(1) || '-',
      player2: player2.stats?.targets?.toFixed(1) || '-'
    },
    {
      stat: 'Receptions/Game',
      player1: player1.stats?.receptions?.toFixed(1) || '-',
      player2: player2.stats?.receptions?.toFixed(1) || '-'
    },
    {
      stat: 'Yards/Game',
      player1: player1.stats?.yards.toFixed(1) || '-',
      player2: player2.stats?.yards.toFixed(1) || '-'
    },
    {
      stat: 'Touchdowns/Game',
      player1: player1.stats?.touchdowns.toFixed(1) || '-',
      player2: player2.stats?.touchdowns.toFixed(1) || '-'
    }
  ] : [];



  const AddButton: React.FC<{ 
    onClick: () => void; 
    children: React.ReactNode; 
  }> = ({ onClick, children }) => (
    <button
      onClick={onClick}
      className="w-full h-32 text-accent-primary hover:text-blue-300 rounded-lg flex flex-col items-center justify-center gap-2 border-2 border-dashed border-border-primary hover:border-text-secondary transition-colors"
    >
      <Plus size={24} />
      <span>{children}</span>
    </button>
  );

  const PlayerCard: React.FC<{
    player: Player | null;
    playerSlot: 'player1' | 'player2';
    title: string;
  }> = ({ player, playerSlot, title }) => {
    if (!player) {
      return (
        <div className="bg-bg-secondary rounded-lg p-6">
          <h2 className="text-xl font-normal font-primary text-text-primary mb-4">{title}</h2>
          <AddButton onClick={() => addPlayer(playerSlot)}>
            Add Player
          </AddButton>
        </div>
      );
    }



    return (
      <div className="bg-bg-secondary rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-normal font-primary text-text-primary">{title}</h2>
          <button 
            onClick={() => removePlayer(playerSlot)}
            className="text-text-secondary hover:text-text-primary transition-colors p-1"
          >
            <X size={20} />
          </button>
        </div>
        
        {/* Player Info - Compact horizontal layout */}
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0">
            {player.photoUrl ? (
              <img
                src={player.photoUrl}
                alt={player.name}
                className="w-16 h-16 rounded-lg object-cover"
                onError={(e) => {
                  // Fallback to initials if image fails to load
                  e.currentTarget.style.display = 'none';
                  e.currentTarget.nextElementSibling?.classList.remove('hidden');
                }}
              />
            ) : null}
            <div className={`flex h-16 w-16 shrink-0 items-center justify-center rounded-lg bg-bg-tertiary text-base font-medium text-text-secondary ${player.photoUrl ? 'hidden' : ''}`}>
              {getInitials(player.name)}
            </div>
          </div>
          <div className="flex-grow min-w-0">
            <h3 className="text-xl font-normal font-primary text-text-primary mb-1">
              {player.name}
            </h3>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-sm font-semibold text-text-primary">
                {player.position}
              </span>
              <span className="text-base text-text-secondary">{player.team}</span>
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2">
              {player.height && (
                <div>
                  <p className="text-text-secondary text-xs mb-0.5">Height</p>
                  <p className="text-text-primary font-medium text-sm">{player.height}</p>
                </div>
              )}
              {player.weight && (
                <div>
                  <p className="text-text-secondary text-xs mb-0.5">Weight</p>
                  <p className="text-text-primary font-medium text-sm">{player.weight}</p>
                </div>
              )}
              {player.age && (
                <div>
                  <p className="text-text-secondary text-xs mb-0.5">Age</p>
                  <p className="text-text-primary font-medium text-sm">{player.age}</p>
                </div>
              )}
              {player.experience && (
                <div>
                  <p className="text-text-secondary text-xs mb-0.5">Experience</p>
                  <p className="text-text-primary font-medium text-sm">{player.experience}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <LayoutWithChat>
      {/* Page Title */}
      <h1 className="text-3xl font-normal font-primary text-text-primary">
        Player Comparison
      </h1>

      {/* Player Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <PlayerCard
          player={player1}
          playerSlot="player1"
          title="Player 1"
        />
        <PlayerCard
          player={player2}
          playerSlot="player2"
          title="Player 2"
        />
      </div>

      {/* AI Comparison */}
      {player1 && player2 && (
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-4">
            <h2 className="text-xl font-normal font-primary text-text-primary">
              AI Comparison
            </h2>
            
            {/* Overall Winner */}
            <div className="flex items-center justify-center p-6 bg-bg-secondary rounded-lg mb-6">
              <div className="text-center">
                <p className="text-lg text-text-secondary mb-2">
                  Better player overall
                </p>
                <p className="text-2xl font-medium text-green-400 mb-4">
                  {analysis.winnerName}
                </p>
                <p className="text-text-secondary max-w-xl mx-auto">
                  {analysis.summary}
                </p>
              </div>
            </div>

            {/* Category Breakdown */}
            <ResponsiveTable
              columns={analysisColumns}
              data={analysis.categories}
              keyExtractor={(category) => category.name}
              cardRenderer={(category) => (
                <div className="bg-bg-secondary border border-border-primary rounded-lg p-4">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="text-base font-medium text-text-primary">
                      {category.name}
                    </h3>
                    <span className={`text-sm font-medium px-2 py-1 rounded ${
                      category.winner === 'player1' ? 'text-green-400 bg-green-400/20' : 'text-blue-400 bg-blue-400/20'
                    }`}>
                      {category.winner === 'player1' ? player1?.name : player2?.name}
                    </span>
                  </div>
                  <p className="text-sm text-text-secondary">
                    {category.reasoning}
                  </p>
                </div>
              )}
            />
          </div>
        </div>
      )}

      {/* Stats Comparison */}
      {player1 && player2 && (
        <div className="flex flex-col gap-4">
          <h2 className="text-xl font-normal font-primary text-text-primary">
            Stats Comparison
          </h2>
          <ResponsiveTable
            columns={statsColumns}
            data={statsData}
            keyExtractor={(stat) => stat.stat}
            cardRenderer={(stat) => (
              <div className="bg-bg-secondary border border-border-primary rounded-lg p-4">
                <h3 className="text-base font-medium text-text-primary mb-3">
                  {stat.stat}
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center">
                    <div className="text-xs text-text-secondary mb-1">
                      {player1?.name}
                    </div>
                    <div className="text-lg font-medium text-text-primary">
                      {stat.player1}
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-xs text-text-secondary mb-1">
                      {player2?.name}
                    </div>
                    <div className="text-lg font-medium text-text-primary">
                      {stat.player2}
                    </div>
                  </div>
                </div>
              </div>
            )}
          />
        </div>
      )}
    </LayoutWithChat>
  );
};

export default PlayerComparison;